import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import JoinRequest from "../components/joinRequest";
import RulesStatement from "../components/rulesStatement";
import Contract from "../components/contract";
import ParentStatement from "../components/parentStatement";
import JoinDecision from "../components/decision";
import CanvasDraw from "react-canvas-draw";
// NEW: osobny dokument dla dorosłych
import AdultContract from "../components/adultContract";

export default function Documentation() {
  const canvasRef = useRef(null);

  const [error, setError] = useState("");
  const [hasSignature, setHasSignature] = useState(false);

  const navigate = useNavigate();
  const [userAcc, setUserAcc] = useState("");
  const [docsView, setDocsView] = useState("to-sign"); // "to-sign" | "signed"

  // aktualny uczestnik i dokument
  const [currentKey, setCurrentKey] = useState(null); // "0" | "1" | "2"
  const [currentDocKey, setCurrentDocKey] = useState(null); // np. "contract" | "adultContract"

  // potwierdzenie zapoznania per userKey + docKey
  const [ackMap, setAckMap] = useState({}); // { [userKey]: { [docKey]: boolean } }

  // lokalne oznaczenie podpisanych w tej sesji (znikają bez czekania na snapshot)
  const [sessionSigned, setSessionSigned] = useState({}); // { [userKey]: Set(docKey) }

  const BRAND = {
    primary: "#0045C7",
    text: "rgb(30,30,30)",
    subtleText: "rgb(100,100,100)",
    cardBg: "#fff",
    cardBorder: "rgba(17,24,39,.08)",
    cardShadow: "0 10px 22px rgba(17,24,39,0.12)",
    ring: "rgba(0,69,199,.12)",
  };

  const ASSETS = {
    rulesKids: process.env.PUBLIC_URL + "/Regulamin2026.pdf",
    terminationTemplate: process.env.PUBLIC_URL + "/Wypowiedzenie umowy 2026.pdf",
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (e) {
      console.error("Błąd podczas wylogowania:", e);
    }
  };

  // === Firestore: subskrypcja konta ===
  useEffect(() => {
    if (!auth?.currentUser) return;
    const unsub = onSnapshot(
      doc(db, "users2026", auth?.currentUser?.uid),
      (snap) => setUserAcc(snap.data())
    );
    return () => unsub && unsub();
  }, []);

  // ===== Helpers bazujące na docs z subUsera (NOWA STRUKTURA) =====
  // Lista wymaganych dokumentów bierze się z tablicy `docs.required`.
  // Dodatkowe zasady: jeśli `adult === true`, pomijamy "parentStatement".
  const requiredDocKeysFor = (u) => {
    if (!u) return [];
    const req = u?.docs?.required;

    // Preferuj tablicę `required`; fallback – weź klucze z docs, ale bez "required".
    let keys = Array.isArray(req)
      ? req.slice()
      : Object.keys(u?.docs || {}).filter((k) => k !== "required");

    // dla dorosłego nie wymagamy oświadczenia rodzica
    if (u?.adult) keys = keys.filter((k) => k !== "parentStatement");

    // nic więcej nie narzucamy — jeśli w required jest "adultContract", zostaje
    return keys;
  };

  const isDocSignedRemote = (u, key) => u?.docs?.[key]?.signed === true;

  const isDocSigned = (userKey, u, key) => {
    const local =
      sessionSigned?.[userKey] instanceof Set &&
      sessionSigned[userKey].has(key);
    return local || isDocSignedRemote(u, key);
  };

  const remainingDocs = (userKey, u) =>
    requiredDocKeysFor(u).filter((k) => !isDocSigned(userKey, u, k));

  const countSignedForUser = (userKey, u) => {
    if (!u) return 0;
    const need = requiredDocKeysFor(u);
    return need.filter((k) => isDocSigned(userKey, u, k)).length;
  };

  const allDocsSigned = () => {
    const subUsers = userAcc?.subUsers || {};
    const keys = Object.keys(subUsers);
    if (!keys.length) return false;
    return keys.every((k) => remainingDocs(k, subUsers[k]).length === 0);
  };

  const docTitle = (docKey, adult) => {
    if (docKey === "joinRequest") return "Wniosek o przyjęcie";
    if (docKey === "rules") return "Oświadczenie – regulamin";
    if (docKey === "contract") return adult ? "Umowa (osoba dorosła)" : "Umowa";
    if (docKey === "adultContract") return "Umowa dla dorosłych";
    if (docKey === "parentStatement") return "Oświadczenie rodzica/opiekuna";
    return docKey;
  };

  // === Inicjalizacja na podstawie „docs” ===
  useEffect(() => {
    const subUsers = userAcc?.subUsers || {};
    const keys = Object.keys(subUsers);
    if (!keys.length) return;

    // 1) ustaw currentKey na pierwszego z brakami
    let firstKey = currentKey;
    if (firstKey === null) {
      firstKey =
        keys.find((k) => remainingDocs(k, subUsers[k]).length > 0) ?? keys[0];
      setCurrentKey(firstKey);
    }

    // 2) zainicjuj ackMap (po docKey z docs)
    const initAck = { ...ackMap };
    keys.forEach((k) => {
      const u = subUsers[k];
      initAck[k] = initAck[k] || {};
      requiredDocKeysFor(u).forEach((dk) => {
        if (typeof initAck[k][dk] !== "boolean") initAck[k][dk] = false;
      });
    });
    setAckMap(initAck);

    // 3) ustaw currentDocKey na pierwszy brakujący z docs
    const u = subUsers[firstKey];
    if (u) {
      const rem = remainingDocs(firstKey, u);
      if (!currentDocKey || !rem.includes(currentDocKey)) {
        setCurrentDocKey(rem[0] || null);
      }
    }

    // 4) ustaw widok
    setDocsView(allDocsSigned() ? "signed" : "to-sign");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAcc?.subUsers, currentKey]);

  // ====== Canvas: wykrycie czy coś narysowano ======
  const detectSignatureDrawn = () => {
    try {
      const raw = canvasRef?.current?.getSaveData?.();
      if (!raw) return false;
      const json = JSON.parse(raw);
      const lines = Array.isArray(json?.lines) ? json.lines : [];
      return lines.some((l) => Array.isArray(l?.points) && l.points.length > 2);
    } catch {
      return false;
    }
  };

  // ====== Akcje UI ======
  const toggleAck = () => {
    if (!currentKey || !currentDocKey) return;
    setAckMap((prev) => ({
      ...prev,
      [currentKey]: {
        ...prev[currentKey],
        [currentDocKey]: !prev?.[currentKey]?.[currentDocKey],
      },
    }));
  };

  const goPrev = () => {
    const u = userAcc?.subUsers?.[currentKey];
    if (!u || !currentDocKey) return;
    const rem = remainingDocs(currentKey, u);
    const idx = rem.indexOf(currentDocKey);
    const prevIdx = idx > 0 ? idx - 1 : 0;
    if (rem.length) setCurrentDocKey(rem[prevIdx]);
  };

  const goNext = () => {
    const subUsers = userAcc?.subUsers || {};
    if (!currentKey || !currentDocKey) return;
    const u = subUsers[currentKey];
    if (!u) return;

    // kolejny brakujący dokument u tej samej osoby
    const rem = remainingDocs(currentKey, u);
    if (rem.length > 0) {
      const idx = rem.indexOf(currentDocKey);
      if (idx >= 0 && idx < rem.length - 1) {
        setCurrentDocKey(rem[idx + 1]);
        return;
      }
      if (idx === -1) {
        setCurrentDocKey(rem[0]);
        return;
      }
    }

    // brak braków – kolejna osoba z brakami
    const keys = Object.keys(subUsers);
    const currentPos = keys.indexOf(currentKey);
    const rotated = [
      ...keys.slice(currentPos + 1),
      ...keys.slice(0, currentPos + 1),
    ];
    const nextKeyWithRem = rotated.find(
      (k) => remainingDocs(k, subUsers[k]).length > 0
    );

    if (nextKeyWithRem) {
      setCurrentKey(nextKeyWithRem);
      setCurrentDocKey(
        remainingDocs(nextKeyWithRem, subUsers[nextKeyWithRem])[0]
      );
      return;
    }

    // Wszyscy gotowi
    setDocsView("signed");
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("pl-PL");
    } catch {
      return "";
    }
  };

  // ====== Zapis podpisu JEDNEGO dokumentu ======
  const submitDocSignature = async () => {
    try {
      if (!currentKey || !currentDocKey) return;

      if (!ackMap?.[currentKey]?.[currentDocKey]) {
        setError("Zaznacz potwierdzenie zapoznania się z dokumentem.");
        return;
      }

      const drawn = detectSignatureDrawn();
      if (!drawn) {
        setError("Najpierw narysuj podpis w polu — wygląda na pusty.");
        return;
      }

      const dataURL = canvasRef?.current?.getDataURL?.();
      if (!dataURL) {
        setError("Nie udało się pobrać podpisu");
        return;
      }

      const uid = auth?.currentUser?.uid;
      if (!uid) return;

      // Minimalny update tylko dla bieżącego dokumentu
      const update = {
        subUsers: {
          [currentKey]: {
            docs: {
              [currentDocKey]: {
                signed: true,
                date: new Date().toISOString(),
                sign: dataURL,
              },
            },
          },
        },
      };

      // Specjalne przypadki: contract oraz adultContract
      const u = userAcc?.subUsers?.[currentKey];

      if (currentDocKey === "contract" && u) {
        const obligation = u?.docs?.contract?.obligation;
        update.subUsers[currentKey].docs.contract = {
          ...update.subUsers[currentKey].docs.contract,
          active: true,
          ...(obligation !== undefined ? { obligation } : {}),
        };
      }

      if (currentDocKey === "adultContract" && u) {
        const obligation = u?.docs?.adultContract?.obligation;
        update.subUsers[currentKey].docs.adultContract = {
          ...update.subUsers[currentKey].docs.adultContract,
          active: true,
          ...(obligation !== undefined ? { obligation } : {}),
        };
      }

      await setDoc(doc(db, "users2026", uid), update, { merge: true });

      // Lokalnie oznacz jako podpisany
      setSessionSigned((prev) => {
        const set = new Set(prev?.[currentKey] || []);
        set.add(currentDocKey);
        return { ...prev, [currentKey]: set };
      });

      // Reset canvas i checkboxa
      canvasRef?.current?.clear();
      setHasSignature(false);
      setAckMap((prev) => ({
        ...prev,
        [currentKey]: { ...prev[currentKey], [currentDocKey]: false },
      }));
    } catch (e) {
      console.error(e);
      setError("Nie udało się zapisać podpisu. Spróbuj ponownie.");
    }
  };

  // ====== RENDER ======
  const subUsers = userAcc?.subUsers || {};
  const currentUser = currentKey !== null ? subUsers?.[currentKey] : null;

  const totalRequiredForUser = currentUser
    ? requiredDocKeysFor(currentUser).length
    : 0;
  const signedForUser = currentUser
    ? countSignedForUser(currentKey, currentUser)
    : 0;
  const remList = currentUser ? remainingDocs(currentKey, currentUser) : [];

  const currentDocSignedRemote =
    currentUser?.docs?.[currentDocKey]?.signed === true;
  const currentDocSigned =
    currentKey && currentDocKey
      ? isDocSigned(currentKey, currentUser, currentDocKey)
      : false;

  const stepNumerator = Math.min(
    signedForUser + 1,
    Math.max(1, totalRequiredForUser)
  );
  const stepDenominator = Math.max(1, totalRequiredForUser);

  const waitingForVerification =
    userAcc &&
    Object.values(subUsers || {}).some((su) => su?.toVerificate === true);

  // === DOWNLOADS (bez rozróżnienia dorosłych) ===
  const subUsersArr = Object.values(subUsers || {});

  const rulesHref = ASSETS.rulesKids;
  const rulesLabel = "Regulamin zajęć 2026";

  const hasStandardContract = subUsersArr.some(
    (u) => u?.docs?.contract?.signed === true && !u?.docs?.contract?.obligation
  );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        backgroundColor: "#F5F7FB",
        width: "100%",
        minHeight: "100vh",
        overflowY: "auto",
        gap: "24px",
      }}
    >
      {/* Toast błędu */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          position: "fixed",
          zIndex: 40,
          top: error ? "46px" : "-300px",
          transform: "translateX(-50%)",
          left: "50%",
          backgroundColor: "#fff",
          width: 360,
          minHeight: 76,
          transition: ".6s",
          borderRadius: 14,
          border: "2px solid rgb(215,0,64)",
          gap: 12,
          padding: "10px 14px",
          boxShadow: "0 10px 24px rgba(17,24,39,.14)",
        }}
      >
        <img
          src={process.env.PUBLIC_URL + "/error.png"}
          style={{ width: 28 }}
          alt=""
        />
        <div style={{ fontSize: 13, fontWeight: 700, color: "rgb(215,0,64)" }}>
          {error}
        </div>
      </div>

      {/* BODY */}
      {waitingForVerification ? (
        // ======= EKRAN WERYFIKACJI =======
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:20,padding:"20px 16px",background:"#F5F7FB",minHeight:"100vh", justifyContent: "flex-start"}}>
          <div style={({width: "min(720px, 92vw)", borderRadius: "18px", padding: "28px", display: "flex", flexDirection: "column", alignItems: "center"})}>
            <div style={({width: "86px", height: "86px", borderRadius: "50%", display: "grid", placeItems: "center", background: "linear-gradient(135deg, #111827 0%, #374151 100%)", boxShadow: "0 10px 22px rgba(17,24,39,0.25)", marginBottom: "14px"})}>
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2.5"/>
                <path d="M12 7v5l3 2" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 style={({fontSize: "30px", color: "rgb(22,22,22)", textAlign: "center", margin: "0 0 8px 0", fontWeight: 700})}>Twoje konto jest w trakcie weryfikacji</h3>
            <h3 style={({fontSize: "16px", color: "rgb(80,80,80)", textAlign: "center", fontWeight: "normal", margin: "0 0 12px 0"})}>
              Dziękujemy za rejestrację! Weryfikacja może potrwać do <span style={({fontWeight: 600})}>48 godzin</span> od założenia konta.
            </h3>
            <hr style={({height: "1px", border: "none", width: "100%", background: "linear-gradient(90deg, rgba(0,0,0,0), rgba(0,0,0,0.08), rgba(0,0,0,0))", margin: "10px 0 14px 0"})} />
            <h3 style={({fontSize: "15px", color: "rgb(55,55,55)", textAlign: "center", fontWeight: "normal", margin: "0 0 6px 0"})}>
              Po zakończeniu weryfikacji wyślemy na Twój e-mail <span style={({fontWeight: 600})}>fakturę do opłaty</span>.
            </h3>
            <h3 style={({fontSize: "15px", color: "rgb(55,55,55)", textAlign: "center", fontWeight: "normal", margin: "0 0 6px 0"})}>
              Po zalogowaniu <span style={({fontWeight: 600})}>po weryfikacji konta</span> podpiszesz <span style={({fontWeight: 600})}>dokumenty online</span> bezpośrednio tutaj.
            </h3>
            <h3 style={({fontSize: "15px", color: "rgb(55,55,55)", textAlign: "center", fontWeight: "normal", margin: "0 0 10px 0"})}>
              Czas weryfikacji zależy od bieżącego obłożenia — prosimy o chwilę cierpliwości.
            </h3>
            <h3 style={({fontSize: "14px", color: "rgb(110,110,110)", textAlign: "center", fontWeight: "normal", margin: "6px 0 4px 0"})}>
              Nie widzisz wiadomości? Sprawdź folder <span style={({fontWeight: 600})}>SPAM/Oferty</span>.
            </h3>
            <h3 style={({fontSize: "14px", color: "rgb(110,110,110)", textAlign: "center", fontWeight: "normal", margin: "0 0 16px 0"})}>
              tel. <span style={({fontWeight: 600})}>505&nbsp;376&nbsp;552</span> · e-mail <span style={({fontWeight: 600})}>4starsdanceaerialstudio@gmail.com</span>
            </h3>
            <h3 style={({fontSize: "15px", color: "#0045C7", fontWeight: "bold", textAlign: "center", margin: "4px 0 0 0", cursor: "pointer"})} onClick={() => handleLogout()}>
              Wyloguj
            </h3>
          </div>
        </div>
      ) : (
        // ======= PANEL DOKUMENTÓW =======
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            width: "92%",
            maxWidth: 1120,
            marginTop: 12,
          }}
        >
          {/* NAGŁÓWEK */}
          <div style={{width:"100%",maxWidth:1050,display:"flex",justifyContent:"center",alignItems:"center", marginTop: "10px", marginBottom: "20px"}}>
            <h2 style={{margin:0,fontSize:24,color:"#111"}}>Dokumentacja</h2>
          </div>

          {/* Intro Card */}
          <div
            style={{
              background: BRAND.cardBg,
              border: `1px solid ${BRAND.cardBorder}`,
              boxShadow: BRAND.cardShadow,
              borderRadius: 16,
              padding: "18px 18px",
            }}
          >
            <div
              style={{
                fontSize: 18,
                color: BRAND.text,
                fontWeight: 800,
                marginBottom: 6,
              }}
            >
              Witaj ponownie!
            </div>
            <div style={{ color: BRAND.subtleText, fontSize: 14, lineHeight: 1.5 }}>
              Przejdź przez dokumenty uczestników i podpisz każdy z nich —
              podpisać należy każdy dokument osobno w przeznaczonym do tego okienku
            </div>
          </div>

          {/* Pasek z uczestnikami (chipsy) */}
          {Object.keys(subUsers).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {Object.keys(subUsers).map((k) => {
                const u = subUsers[k];
                const totalReq = requiredDocKeysFor(u).length;
                const progress = countSignedForUser(k, u);
                const done = progress === totalReq && totalReq > 0;

                return (
                  <button
                    key={k}
                    style={{
                      all: "unset",
                      cursor: "pointer",
                      background: "#fff",
                      border: `2px solid ${
                        currentKey === k ? BRAND.primary : "rgba(17,24,39,.12)"
                      }`,
                      padding: "10px 12px",
                      borderRadius: 12,
                      boxShadow:
                        currentKey === k
                          ? "0 6px 14px rgba(0,69,199,.18)"
                          : BRAND.cardShadow,
                      minWidth: 170,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: BRAND.primary,
                          display: "grid",
                          placeItems: "center",
                          boxShadow: "0 6px 12px rgba(0,69,199,.35)",
                          flexShrink: 0,
                        }}
                      >
                        <div style={iconCircle("#0A66FF")} aria-hidden>
                          {personSvg("#fff")}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 800,
                            color: BRAND.text,
                            marginLeft: '10px'
                          }}
                        >
                          {u?.displayName || `Uczestnik ${k}`}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: BRAND.subtleText,
                            marginTop: 2,
                            marginLeft: '10px'
                          }}
                        >
                          {progress}/{totalReq} podpisane
                          {done ? " ✓" : ""}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Kroki i podpisy – tylko brakujące wg docs */}
          {docsView === "to-sign" && currentUser && remList.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
                background: BRAND.cardBg,
                border: `1px solid ${BRAND.cardBorder}`,
                boxShadow: BRAND.cardShadow,
                borderRadius: 16,
                padding: 18,
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  color: BRAND.text,
                  fontWeight: 800,
                }}
              >
                {currentUser?.displayName} — krok {stepNumerator}/
                {stepDenominator}: {docTitle(currentDocKey, currentUser?.adult)}
              </div>

              {/* Pasek postępu */}
              <div
                style={{
                  width: "100%",
                  height: 10,
                  background: "rgba(0,0,0,.06)",
                  borderRadius: 10,
                  overflow: "hidden",
                  boxShadow: "inset 0 1px 2px rgba(0,0,0,.06)",
                }}
              >
                <div
                  style={{
                    width: `${
                      (signedForUser / Math.max(1, totalRequiredForUser)) * 100
                    }%`,
                    height: "100%",
                    background:
                      "linear-gradient(90deg, #0045C7 0%, #2F6CF2 100%)",
                    borderRadius: 10,
                    transition: "width .35s",
                  }}
                />
              </div>

              {/* Render treści dokumentu wg docKey */}
              {currentDocKey === "joinRequest" && (
                <JoinRequest displayName={currentUser?.displayName} />
              )}

              {currentDocKey === "rules" && (
                <RulesStatement
                  displayName={currentUser?.displayName}
                  facility={userAcc?.facility_id}
                  date={new Date()?.toLocaleDateString("pl-PL")}
                />
              )}

              {currentDocKey === "contract" && (
                <Contract
                  user={currentUser}
                  parent={userAcc?.parent}
                  acc={userAcc}
                  date={new Date()?.toLocaleDateString("pl-PL")}
                />
              )}

              {/* NEW: adultContract – osobny komponent */}
              {currentDocKey === "adultContract" && (
                <AdultContract
                  user={currentUser}
                  parent={userAcc?.parent}
                  acc={userAcc}
                  date={new Date()?.toLocaleDateString("pl-PL")}
                />
              )}

              {currentDocKey === "parentStatement" && !currentUser?.adult && (
                <ParentStatement
                  user={currentUser}
                  parent={userAcc?.parent}
                  acc={userAcc}
                  date={new Date()?.toLocaleDateString("pl-PL")}
                />
              )}

              {currentDocKey &&
                !["joinRequest", "rules", "contract", "adultContract", "parentStatement"].includes(
                  currentDocKey
                ) && (
                  <div style={{ color: BRAND.subtleText, fontSize: 14 }}>
                    Ten dokument (<b>{currentDocKey}</b>) nie działa, zgłoś błąd mailowo
                  </div>
                )}

              {/* Checkbox zapoznania */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "rgba(0,69,199,.06)",
                  border: `1px solid ${BRAND.ring}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!ackMap?.[currentKey]?.[currentDocKey]}
                  onChange={toggleAck}
                />
                <span style={{ fontSize: 13, color: BRAND.text }}>
                  Potwierdzam, że zapoznałem(-am) się z treścią tego dokumentu
                </span>
              </label>

              {/* Sekcja podpisu */}
              <div
                style={{
                  padding: 14,
                  border: "1px dashed rgba(17,24,39,.25)",
                  borderRadius: 12,
                  backgroundColor: "#fff",
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    color: BRAND.text,
                    fontWeight: 800,
                    marginBottom: 6,
                  }}
                >
                  Podpis tego dokumentu
                </div>

                {currentDocSignedRemote && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "rgb(0,120,70)",
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    ✅ Podpisano: {fmtDate(currentUser?.docs?.[currentDocKey]?.date)}
                  </div>
                )}

                {!currentDocSigned && (
                  <>
                    <div style={{ fontSize: 13, color: BRAND.text, marginBottom: 6 }}>
                      Pole na podpis
                    </div>
                    <CanvasDraw
                      ref={canvasRef}
                      brushColor="black"
                      brushRadius={1.6}
                      lazyRadius={0}
                      hideInterface
                      hideGrid
                      onChange={() => setHasSignature(detectSignatureDrawn())}
                      style={{
                        backgroundColor: "transparent",
                        width: "100%",
                        height: 130,
                        maxWidth: 460,
                        border: "1px solid rgba(17,24,39,.35)",
                        borderRadius: 8,
                      }}
                    />
                    <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                      <button
                        onClick={() => {
                          canvasRef?.current?.clear();
                          setHasSignature(false);
                        }}
                        style={{
                          padding: "8px 12px",
                          borderRadius: 10,
                          fontSize: 12,
                          fontWeight: 700,
                          border: `2px solid ${BRAND.primary}`,
                          color: BRAND.primary,
                          background: "#fff",
                          cursor: "pointer",
                        }}
                      >
                        Wyczyść
                      </button>

                      <button
                        onClick={submitDocSignature}
                        disabled={
                          !ackMap?.[currentKey]?.[currentDocKey] || !hasSignature
                        }
                        style={{
                          padding: "8px 12px",
                          borderRadius: 10,
                          fontSize: 12,
                          fontWeight: 800,
                          border: "none",
                          color: "#fff",
                          background:
                            !ackMap?.[currentKey]?.[currentDocKey] || !hasSignature
                              ? "rgba(0,69,199,.35)"
                              : BRAND.primary,
                          boxShadow:
                            !ackMap?.[currentKey]?.[currentDocKey] || !hasSignature
                              ? "none"
                              : "0 8px 18px rgba(0,69,199,.35)",
                          cursor:
                            !ackMap?.[currentKey]?.[currentDocKey] || !hasSignature
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        Zapisz podpis dokumentu
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Nawigacja – Dalej tylko po podpisie */}
              <div style={{ display: "flex", gap: 10, marginTop: 2 }}>
                <button
                  onClick={goPrev}
                  disabled={remainingDocs(currentKey, currentUser).length === 0}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 700,
                    border: `2px solid ${BRAND.primary}`,
                    color:
                      remainingDocs(currentKey, currentUser).length === 0
                        ? "rgba(0,69,199,.45)"
                        : BRAND.primary,
                    background: "#fff",
                    cursor:
                      remainingDocs(currentKey, currentUser).length === 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  Wstecz
                </button>
                <button
                  onClick={goNext}
                  disabled={!currentDocSigned}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 800,
                    border: "none",
                    color: "#fff",
                    background: !currentDocSigned
                      ? "rgba(0,69,199,.35)"
                      : BRAND.primary,
                    boxShadow: !currentDocSigned
                      ? "none"
                      : "0 8px 18px rgba(0,69,199,.35)",
                    cursor: !currentDocSigned ? "not-allowed" : "pointer",
                  }}
                >
                  Dalej
                </button>
              </div>
            </div>
          )}

          {/* Gdy dla osoby nic nie zostało */}
          {docsView === "to-sign" && currentUser && remList.length === 0 && (
            <div
              style={{
                background: BRAND.cardBg,
                border: `1px solid ${BRAND.cardBorder}`,
                boxShadow: BRAND.cardShadow,
                borderRadius: 16,
                padding: 16,
                color: BRAND.text,
                fontSize: 15,
              }}
            >
              Wszystkie dokumenty dla <b>{currentUser?.displayName}</b> są
              podpisane. Wybierz innego uczestnika z listy powyżej.
            </div>
          )}

          {/* Widok po podpisaniu wszystkich wymaganych dokumentów */}
          {docsView === "signed" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: "50px"}}>
              <div
                style={{
                  fontSize: 18,
                  color: BRAND.text,
                  fontWeight: 800,
                }}
              >
                Podpisana dokumentacja:
              </div>

              {userAcc &&
                Object?.entries(subUsers)?.map(([k, u]) => {
                  const totalReq = requiredDocKeysFor(u).length;
                  return (
                    <div
                      key={k}
                      style={{
                        background: BRAND.cardBg,
                        border: `1px solid ${BRAND.cardBorder}`,
                        boxShadow: BRAND.cardShadow,
                        borderRadius: 16,
                        padding: 16,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 16,
                          color: BRAND.text,
                          fontWeight: 800,
                          marginBottom: 8,
                        }}
                      >
                        {u?.displayName} — {countSignedForUser(k, u)}/{totalReq}{" "}
                        podpisane
                      </div>

                      {u?.docs?.joinRequest?.signed && (
                        <JoinDecision
                          displayName={u?.displayName}
                          date={fmtDate(u?.docs?.contract?.date)}
                          signing={u?.docs?.contract?.sign}
                        />
                      )}

                      {/* Podgląd „dziecięcej” umowy – jeśli podpisana */}
                      {!u?.adult && u?.docs?.contract?.signed && (
                        <Contract
                          user={u}
                          parent={userAcc?.parent}
                          acc={userAcc}
                          date={fmtDate(u?.docs?.contract?.date)}
                          signing={u?.docs?.contract?.sign}
                        />
                      )}

                      {/* Podgląd „dorosłej” umowy – jeśli podpisana */}
                      {u?.adult && u?.docs?.adultContract?.signed && (
                        <AdultContract
                          user={u}
                          acc={userAcc}
                          parent={userAcc?.parent}
                          date={fmtDate(u?.docs?.adultContract?.date)}
                          signing={u?.docs?.adultContract?.sign}
                        />
                      )}

                      {!u?.adult && u?.docs?.parentStatement?.signed && (
                        <ParentStatement
                          user={u}
                          parent={userAcc?.parent}
                          acc={userAcc}
                          signing={u?.docs?.parentStatement?.sign}
                          date={fmtDate(u?.docs?.parentStatement?.date)}
                        />
                      )}

                      {u?.docs?.rules?.signed && (
                        <RulesStatement
                          displayName={u?.displayName}
                          facility={userAcc?.facility_id}
                          signing={u?.docs?.rules?.sign}
                          date={fmtDate(u?.docs?.rules?.date)}
                        />
                      )}
                    </div>
                  );
                })}
              {/* === Pliki do pobrania === */}
              <div
                style={{
                  background: BRAND.cardBg,
                  border: `1px solid ${BRAND.cardBorder}`,
                  boxShadow: BRAND.cardShadow,
                  borderRadius: 16,
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div style={{ fontSize: 16, color: BRAND.text, fontWeight: 800 }}>
                  Pliki do pobrania
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {/* Regulamin */}
                  <a
                    href={ASSETS.rulesKids}
                    target="_blank"
                    rel="noreferrer"
                    download
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      textDecoration: "none",
                      padding: "10px 12px",
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 800,
                      color: "rgb(30,30,30)",
                      width: 280,
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={process.env.PUBLIC_URL + "/emptyFile.png"}
                      alt=""
                      style={{ width: 20 }}
                    />
                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      Regulamin
                    </span>
                  </a>

                  {/* Wypowiedzenie umowy – tylko jeśli ktoś ma standardową umowę */}
                  {hasStandardContract && (
                    <a
                      href={ASSETS.terminationTemplate}
                      target="_blank"
                      rel="noreferrer"
                      download
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        textDecoration: "none",
                        padding: "10px 12px",
                        borderRadius: 10,
                        fontSize: 12,
                        fontWeight: 800,
                        color: "rgb(30,30,30)",
                        width: 280,
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src={process.env.PUBLIC_URL + "/emptyFile.png"}
                        alt=""
                        style={{ width: 20 }}
                      />
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        Wypowiedzenie umowy
                      </span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const iconCircle = (bg) => ({
  width:30, height:30, borderRadius:12, display:"grid", placeItems:"center",
  background:bg
});

const personSvg = (fill="#fff") => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Z" fill={fill}/>
    <path d="M4 21a8 8 0 0 1 16 0" fill="none" stroke={fill} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
